import { JSX, useRef, useState } from 'react';

import { Box, Container, Stack, Typography } from '@mui/material';
import cx from 'clsx';
import { Swiper as SwiperType } from 'swiper';

import CarouselButton from '@/components/CarouselButton';
import ChevronLeft from '@/components/SvgIcons/ChevronLeft';
import ChevronRight from '@/components/SvgIcons/ChevronRight';
import colors from '@/styles/themes/colors';
import useBreakpoint from '@/utils/hooks/useBreakpoint';

import styles from './SliderSection.module.scss';

interface SliderSectionProps {
  title?: string;
  titleVariant?: 'h2' | 'h3' | 'h4';
  showArrows?: boolean;
  SliderComponent?: (props: { handleSwiper: (swiper: SwiperType) => void }) => JSX.Element;
  children?: (handleSwiper: (swiper: SwiperType) => void) => JSX.Element;
  customStyles?: {
    container?: string;
    overlay?: string;
  };
}

const SliderSection = ({
  title,
  titleVariant = 'h2',
  showArrows = true,
  SliderComponent,
  children,
  customStyles,
}: SliderSectionProps) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const { isMobile } = useBreakpoint();

  const handleSwiper = (swiper: SwiperType) => {
    swiperRef.current = swiper;
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);

    const updateState = () => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    };

    swiper.on('slideChange', updateState);
    swiper.on('reachBeginning', updateState);
    swiper.on('reachEnd', updateState);
    swiper.on('fromEdge', updateState);
  };

  const handleSwiperNext = () => {
    swiperRef.current?.slideNext();
  };

  const handleSwiperPrev = () => {
    swiperRef.current?.slidePrev();
  };

  return (
    <Container
      component="section"
      maxWidth={false}
      disableGutters
      classes={{ root: styles.root }}
      className={cx(styles.container, customStyles?.container)}
    >
      <Container maxWidth="xl">
        {title && (
          <Typography variant={titleVariant} component="h2" color={colors.white}>
            {title}
          </Typography>
        )}
        <Box className={styles.sliderWrapper}>
          {SliderComponent ? <SliderComponent handleSwiper={handleSwiper} /> : children?.(handleSwiper)}
        </Box>
      </Container>
      {showArrows && !isMobile && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" className={styles.arrowsWrapper}>
          {!isBeginning ? (
            <CarouselButton icon={ChevronLeft} onClick={handleSwiperPrev} ariaLabel="Previous slide" />
          ) : (
            <Box />
          )}
          {!isEnd ? <CarouselButton icon={ChevronRight} onClick={handleSwiperNext} ariaLabel="Next slide" /> : <Box />}
        </Stack>
      )}
      {!isBeginning && <Box className={cx(styles.overlayLeft, customStyles?.overlay)} />}
      {!isEnd && <Box className={cx(styles.overlayRight, customStyles?.overlay)} />}
    </Container>
  );
};

export default SliderSection;
